export const USER_ADDRESS_TYPE = {
  RESIDENTIAL: 1,
  BUSINESS: 2,
  OTHER: 3,
  INDUSTRIAL: 5,
  REMOTE_SITE: 6,
} as const

export type UserAddressExtendedFields = {
  access_instructions?: string | null
  location_pin?: string | null
  entry_code?: string | null
  opening_time?: string | null
  closing_time?: string | null
  saturday_closed?: boolean
  sunday_closed?: boolean
}

export const getAddressTypeLabel = (value?: number | null) => {
  if (value === USER_ADDRESS_TYPE.REMOTE_SITE) {
    return 'Remote Site'
  }
  if (value === USER_ADDRESS_TYPE.INDUSTRIAL) {
    return 'Industrial'
  }
  if (value === USER_ADDRESS_TYPE.OTHER) {
    return 'Other'
  }
  if (value === USER_ADDRESS_TYPE.BUSINESS) {
    return 'Business'
  }
  return 'Residential'
}

export const getAddressTypeNameForPayload = (value?: number | null, otherLabel?: string) => {
  if (value === USER_ADDRESS_TYPE.OTHER) {
    return otherLabel?.trim() || 'Other Address'
  }
  return `${getAddressTypeLabel(value)} Address`
}

export const normalizeAddressTypeForForm = (value?: number | null) => {
  if (value === USER_ADDRESS_TYPE.REMOTE_SITE) {
    return USER_ADDRESS_TYPE.REMOTE_SITE
  }
  if (value === USER_ADDRESS_TYPE.INDUSTRIAL) {
    return USER_ADDRESS_TYPE.INDUSTRIAL
  }
  if (value === USER_ADDRESS_TYPE.OTHER) {
    return USER_ADDRESS_TYPE.OTHER
  }
  if (value === USER_ADDRESS_TYPE.BUSINESS) {
    return USER_ADDRESS_TYPE.BUSINESS
  }
  return USER_ADDRESS_TYPE.RESIDENTIAL
}

export const toTimeInputValue = (value?: string | null) => {
  if (!value) {
    return ''
  }

  const trimmed = value.trim()
  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed
  }

  const match = trimmed.match(/^(\d{2}):(\d{2})/)
  return match ? `${match[1]}:${match[2]}` : ''
}

export const formatTimeLabel = (value?: string | null) => {
  const inputValue = toTimeInputValue(value)
  if (!inputValue) {
    return null
  }

  const [hoursText, minutesText] = inputValue.split(':')
  const hours = Number(hoursText)
  const minutes = Number(minutesText)
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return inputValue
  }

  const period = hours >= 12 ? 'PM' : 'AM'
  const hour12 = hours % 12 || 12
  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`
}

export const isBusinessHoursAddressType = (value?: number | null) => (
  value === USER_ADDRESS_TYPE.BUSINESS
  || value === USER_ADDRESS_TYPE.INDUSTRIAL
  || value === USER_ADDRESS_TYPE.REMOTE_SITE
)

export const buildAddressAccessSummary = (address?: UserAddressExtendedFields | null) => {
  if (!address) {
    return []
  }

  const lines: string[] = []
  if (address.access_instructions) {
    lines.push(`Access: ${address.access_instructions}`)
  }
  if (address.location_pin || address.entry_code) {
    const pinParts = [
      address.location_pin ? `Pin: ${address.location_pin}` : null,
      address.entry_code ? `Entry code: ${address.entry_code}` : null,
    ].filter(Boolean)
    lines.push(pinParts.join(' · '))
  }
  if (address.opening_time || address.closing_time) {
    const hours = [formatTimeLabel(address.opening_time), formatTimeLabel(address.closing_time)].filter(Boolean).join(' – ')
    const weekend = [
      address.saturday_closed ? 'Sat closed' : null,
      address.sunday_closed ? 'Sun closed' : null,
    ].filter(Boolean).join(', ')
    lines.push(`Hours: ${hours}${weekend ? ` (${weekend})` : ''}`)
  } else if (address.saturday_closed || address.sunday_closed) {
    const weekend = [
      address.saturday_closed ? 'Closed Saturday' : null,
      address.sunday_closed ? 'Closed Sunday' : null,
    ].filter(Boolean).join(' · ')
    lines.push(weekend)
  }
  return lines
}
